import { z } from 'zod';
import {
  birthdateSchema,
  emailSchema,
  firstNameSchema,
  identificationSchema,
  lastNameSchema,
  phoneSchema
} from './common/schemas';

const userSchema = (props: { minAge?: number; maxAge?: number }) =>
  z
    .object({
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      identification: identificationSchema,
      birthdate: birthdateSchema(props),
      location: z.string().min(1, 'schemas:common.fieldRequired'),
      insurance: z.string().min(1, 'schemas:common.fieldObligatory'),
      holidayDate: z.boolean({
        required_error: 'schemas:common.fieldObligatory',
        invalid_type_error: 'schemas:common.fieldObligatory'
      }),
      holidayDateDetails: z.string().optional(),
      isEmployeePartner: z.string().min(1, 'schemas:common.fieldObligatory'),
      isPartner: z.boolean({
        required_error: 'schemas:common.fieldObligatory'
      }),
      isExtension: z.boolean({
        required_error: 'schemas:common.fieldObligatory'
      })
    })
    .refine(
      (data) => {
        if (data.holidayDate === true) {
          return (data.holidayDateDetails ?? '').trim().length > 0;
        }
        return true;
      },
      {
        path: ['holidayDateDetails'],
        message: 'schemas:common.fieldRequired'
      }
    );

const questionsSchema = z.discriminatedUnion(
  'value',
  [
    z.object({
      question: z.string(),
      placeholder: z.string().optional(),
      value: z.literal<boolean>(true, {
        invalid_type_error: 'schemas:common.fieldObligatory'
      }),
      answer: z
        .string({ required_error: 'schemas:common.fieldObligatory' })
        .min(1, 'schemas:common.fieldRequired')
    }),
    z.object({
      question: z.string(),
      value: z.literal<boolean>(false, {
        invalid_type_error: 'schemas:common.fieldObligatory'
      }),
      answer: z
        .string()
        .optional()
        .transform(() => '')
    })
  ],
  {
    errorMap: (issue, ctx) => ({
      message:
        issue.code === 'invalid_union_discriminator'
          ? `schemas:common.fieldObligatory`
          : ctx.defaultError
    })
  }
);
export type TQuestionsSchema = z.infer<typeof questionsSchema>;

const authorizedToPickUpSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  identification: identificationSchema,
  phone: phoneSchema
});

const parentsSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  connection: z.string().min(1, 'schemas:common.fieldRequired'),
  identification: identificationSchema,
  phone: phoneSchema,
  email: emailSchema
});

const formParticipantDataVpSchema = ({
  minAge,
  maxAge
}: {
  minAge?: number;
  maxAge?: number;
}) => {
  return z.object({
    user: userSchema({ minAge, maxAge }),
    parents: z.array(parentsSchema),
    authorizedToPickUp: authorizedToPickUpSchema,
    experience: z.string().min(1, 'schemas:common.fieldRequired'),
    diseases: z.array(questionsSchema),
    treatments: z.array(questionsSchema)
  });
};

export default formParticipantDataVpSchema;
